import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { X } from 'lucide-react';

const EMOJI_CATEGORIES = {
  'Pessoas': ['😊', '😎', '🥳', '🤩', '😇', '🧑‍🎤', '🧑‍🚀', '🧑‍✈️', '🦸', '🦹', '🧙', '🧚', '🧜', '🧝', '🥷', '👸', '🤴', '🎅'],
  'Aventura': ['🏄', '🚴', '🧗', '🏂', '🪂', '🏊', '🚣', '⛷️', '🤿', '🏇', '🪁', '🛹'],
  'Viagem': ['✈️', '🚀', '🛸', '🚢', '🚂', '🚁', '🏖️', '🏔️', '🏕️', '🗺️', '🧳', '🎒'],
  'Comida': ['🍕', '🍔', '🌮', '🍣', '🍜', '🍦', '🍹', '🍷', '☕', '🧁', '🥐', '🍩'],
  'Natureza': ['🌴', '🌺', '🌻', '🌈', '⭐', '🌙', '☀️', '🌊', '🔥', '❄️', '🍀', '🌵'],
  'Animais': ['🦋', '🐬', '🦜', '🐢', '🦩', '🐙', '🦈', '🐋', '🦄', '🐉', '🦁', '🐯'],
};

export const EmojiPicker = ({ selectedEmoji, onSelect, onClose }) => {
  const [activeCategory, setActiveCategory] = useState('Pessoas');

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden w-80"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-slate-100 dark:border-slate-700">
        <h3 className="font-semibold text-slate-900 dark:text-white">Escolha seu avatar</h3>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Selected Preview */}
      <div className="flex items-center justify-center py-4 bg-gradient-to-br from-orange-50 to-pink-50 dark:from-orange-900/20 dark:to-pink-900/20">
        <motion.div
          key={selectedEmoji}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="w-20 h-20 rounded-full bg-white dark:bg-slate-700 shadow-lg flex items-center justify-center text-5xl"
        >
          {selectedEmoji || '😊'}
        </motion.div>
      </div>

      {/* Category Tabs */}
      <div className="flex overflow-x-auto p-2 gap-1 border-b border-slate-100 dark:border-slate-700 scrollbar-hide">
        {Object.keys(EMOJI_CATEGORIES).map((category) => (
          <button
            key={category}
            onClick={() => setActiveCategory(category)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              activeCategory === category
                ? 'bg-orange-500 text-white'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Emoji Grid */}
      <div className="p-3 grid grid-cols-6 gap-2 max-h-48 overflow-y-auto">
        <AnimatePresence mode="popLayout">
          {EMOJI_CATEGORIES[activeCategory].map((emoji, index) => (
            <motion.button
              key={emoji}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ delay: index * 0.02 }}
              onClick={() => onSelect(emoji)}
              className={`w-10 h-10 rounded-lg flex items-center justify-center text-2xl transition-all hover:scale-110 ${
                selectedEmoji === emoji
                  ? 'bg-orange-100 dark:bg-orange-900/50 ring-2 ring-orange-500'
                  : 'hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              {emoji}
            </motion.button>
          ))}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export const EmojiAvatarButton = ({ emoji, size = 'md', onClick, className = '' }) => {
  const sizes = {
    sm: 'w-8 h-8 text-lg',
    md: 'w-12 h-12 text-2xl',
    lg: 'w-16 h-16 text-3xl',
    xl: 'w-20 h-20 text-4xl',
  };

  return (
    <button
      onClick={onClick}
      className={`${sizes[size]} rounded-full bg-gradient-to-br from-orange-100 to-pink-100 dark:from-orange-900/30 dark:to-pink-900/30 flex items-center justify-center transition-transform hover:scale-105 active:scale-95 ${className}`}
    >
      {emoji || '😊'}
    </button>
  );
};

export default EmojiPicker;
